import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { copy } from '../../lib/copy';
import { isIframe } from '../../lib/is-iframe';
import { sleep } from '../../lib/sleep';
import { AppConfigService } from '../../providers/config.service';
import { BrowserService } from '../../services/browser.service';
import { DataLoaderService } from '../../services/data-loader.service';
import { StateService } from '../../services/state.service';
import { StorageService } from '../../services/storage.service';
import { ToastService } from '../../services/toast.service';

import { Clerk } from '@clerk/clerk-js/headless';
import { ExternalAccountResource } from '@clerk/types';
import { captureException, captureMessage } from '@sentry/angular-ivy';
import { GROWTHBOOK } from '../../growthbook';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  clerk: Clerk;
  isIframe: boolean;
  pageLoadFromClerkSync = false;

  private growthBook = inject(GROWTHBOOK);

  constructor(
    private state: StateService,
    private loader: DataLoaderService,
    private config: AppConfigService,
    private cookieService: CookieService,
    private storage: StorageService,
    private browserService: BrowserService,
    private toasts: ToastService,
    @Inject(PLATFORM_ID) private platformId: string
  ) {
    if (!isPlatformBrowser(this.platformId)) return; // Don't do this on the server
    this.pageLoadFromClerkSync = location.search.includes('__clerk_synced=true');
    this.isIframe = isIframe();
    this.tokenInit();
    this.state.logout = () => {
      return this.logout();
    };
  }

  reportedClerkError = false;

  private getClerkConfig() {
    const isSatellite = !!this.browserService.getTenantCustomDomain() && this.growthBook.isOn('custom-domain-auth');

    const domain = isSatellite
      ? this.growthBook.getFeatureValue('custom-domain-domain-override', location.hostname)
      : undefined;

    const settings: unknown = {
      signInUrl: this.config.correctPicflowUrl + '/auth/login',
      signUpUrl: this.config.correctPicflowUrl + '/auth/signup',
      touchSession: false,
      telemetry: false,
      isSatellite,
    };

    return { domain, isSatellite, settings };
  }

  private async clerkInit() {
    try {
      const { domain, isSatellite, settings } = this.getClerkConfig();

      this.clerk = new Clerk(this.config.clerkKey, { domain });
      await this.clerk.load(settings);

      this.state.impersonating = !!this.clerk.session?.actor;

      // This is how we find out if clerk will redirect to the sync url. To avoid flashing the page, we await here while clerk redirects us.
      if (isSatellite && !this.clerk.session && !this.pageLoadFromClerkSync) {
        console.log('Waiting for clerk to redirect to sync session');
        console.log('this.clerk.loaded', this.clerk.loaded);
        console.log('this.clerk.__internal_last_error', this.clerk.__internal_last_error);
        await sleep(10_000);
      }
    } catch (err) {
      if (this.reportedClerkError) return;
      this.reportedClerkError = true;
      console.error('clerk failed to load', err);
      captureMessage('clerk failed to load');
      this.toasts.add('Failed to load authentication service', 'error', { duration: 10_000 });
    }
  }

  private async initClerkWithComponents() {
    if (isPlatformServer(this.platformId)) return;
    return this.loader.get('clerkWithComponentsInit', async () => {
      const { domain, settings } = this.getClerkConfig();
      const { Clerk } = await import('@clerk/clerk-js');
      this.clerk = new Clerk(this.config.clerkKey, { domain });
      await this.clerk.load(settings);
      return true;
    });
  }

  async init() {
    if (!isPlatformBrowser(this.platformId)) return; // Don't do this on the server
    return this.loader.get('authInit', async () => {
      await this.clerkInit();
      this.clerk.addListener(e => {
        console.debug('auth event', e);
      });
      return true;
    });
  }

  getTokenInitError() {
    if (this.isIframe && !this.browserService.isExtension()) return 'iframe';
    if (this.browserService.getTenantCustomDomain() && !this.growthBook.isOn('custom-domain-auth')) {
      return 'tenantDomain';
    }
  }

  async tokenInit() {
    if (this.getTokenInitError()) return;
    return this.loader.get('authData', this.checkSession.bind(this));
  }

  public async mountSignUp(el: HTMLDivElement, email?: string, returnUrl?: string, ref?: string) {
    await this.initClerkWithComponents();

    this.clerk?.mountSignUp(el, {
      initialValues: { emailAddress: email },
      redirectUrl: window.location.origin + '/auth/authorize?returnUrl=' + encodeURIComponent(returnUrl || ''),
      unsafeMetadata: { ref },
    });
  }

  public async unmountSignUp(el: HTMLDivElement) {
    await this.initClerkWithComponents();
    this.clerk?.unmountSignUp(el);
  }

  public async mountSignIn(el: HTMLDivElement, returnUrl?: string) {
    await this.initClerkWithComponents();
    this.clerk?.mountSignIn(el, {
      redirectUrl: window.location.origin + '/auth/authorize?returnUrl=' + encodeURIComponent(returnUrl || ''),
    });
  }

  public async unmountSignIn(el: HTMLDivElement) {
    await this.initClerkWithComponents();
    this.clerk?.unmountSignIn(el);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async logout(message?: string) {
    await this.init();
    await this.clerk.signOut();

    this.storage.remove('activeTenantId');
    this.storage.remove('backupTenantId');
    this.cookieService.delete('picflow-referral-code', '/');
    this.cookieService.delete('picflow-loggedin', '/');
    this.storage.remove('promo');
    window.location.href = `${window.location.origin}/auth/login`;
  }

  async checkSession() {
    if (!isPlatformBrowser(this.platformId)) return; // Don't do this on the server
    if (this.isIframe && !this.browserService.isExtension()) return;
    await this.init();
    if (!this.clerk) return;
    return this.checkClerkSession();
  }

  private async checkClerkSession() {
    const session = this.clerk.session;

    if (!session) {
      console.debug('No clerk session exists');
      return false;
    }

    this.state.tokenData = {
      expiresAt: session.expireAt.getTime(),
      expiresIn: session.expireAt.getTime() - Date.now(),
      getIdToken: () => this.getAuthToken(),
    };

    console.debug('Got new token', copy(session));
    return await session.getToken();
  }
  public async getTokenData() {
    await this.tokenInit();
    return this.state.tokenData;
  }

  public async getAuthToken(isRetry = false) {
    if (this.clerk.session) {
      return await this.clerk.session.getToken({ leewayInSeconds: 2 });
    }

    if (!isRetry) {
      await sleep(1_000);
      return this.getAuthToken(true);
    }

    console.debug('user after timeout', this.clerk.user);
    captureException('no_clerk_session');
    await sleep(100);
    // eslint-disable-next-line no-debugger
    debugger;
    location.reload();
    return;
  }

  public async getTokenFromTemplate(templateName: string) {
    const session = this.clerk.session;
    if (session) return session.getToken({ template: templateName });
  }

  public async getLinkedAccounts() {
    return this.loader.get('linkedAccounts', async () => {
      return this.clerk.user.externalAccounts;
    });
  }

  public async connectSocialAccount() {
    const account = await this.clerk.user.createExternalAccount({
      strategy: 'oauth_google',
      redirectUrl: window.location.origin + '/a/settings/profile',
    });
    location.href = account.verification.externalVerificationRedirectURL.href;
    this.loader.remove('linkedAccounts');
  }

  public async disconnectSocialAccount(account: ExternalAccountResource) {
    await account.destroy();
    this.loader.remove('linkedAccounts');
  }

  public async addPasswordToSocialAccount(newPassword: string) {
    await this.clerk.user.updatePassword({ newPassword });
  }

  public async isSsoUser() {
    await this.init();
    if (!this.clerk) return false; // clerk failed to load
    return this.clerk.user.samlAccounts.length > 0;
  }
}
